import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useToast } from '../ui';
import { playChime } from '../lib/notifySound';
import type { MsgRef, NotificationItem, Selection } from './types';

/**
 * The notification hub on the client. While a user is logged in it polls GET /api/notifications,
 * and on a *rising* unread edge (a genuinely new arrival mid-session) it chimes and pops a toast —
 * that is the "on-screen notification + sound" requirement. It also owns the messaging panel's
 * open/selection state so a notification tap can open the right conversation. Delivery is polling:
 * the app is pull-based (no sockets), so this is the single place that pull lives.
 */

const POLL_MS = 20_000;

interface NotificationCtx {
  items: NotificationItem[];
  unread: number;
  reload: () => Promise<void>;
  markRead: (ids?: number[]) => Promise<void>;
  /** Messaging panel: null = closed, otherwise the open conversation. */
  panel: Selection | null;
  /** the record the composed message is ABOUT (set when opened from a deal etc.), or null. */
  composeRef: MsgRef | null;
  openMessaging: (sel?: Selection, ref?: MsgRef | null) => void;
  clearComposeRef: () => void;
  closeMessaging: () => void;
}

const Ctx = createContext<NotificationCtx | null>(null);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const toast = useToast();
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unread, setUnread] = useState(0);
  const [panel, setPanel] = useState<Selection | null>(null);
  const [composeRef, setComposeRef] = useState<MsgRef | null>(null);
  const prevUnread = useRef(0);
  const primed = useRef(false); // skip the chime on the very first load of a session
  const reqSeq = useRef(0); // guards against a stale poll overwriting a fresher markRead

  const reload = useCallback(async () => {
    const seq = ++reqSeq.current;
    const r = await api<{ items: NotificationItem[]; unread: number }>('/api/notifications');
    // Drop a stale response: a newer poll — or a markRead — superseded this one while it was in flight.
    if (!r.ok || seq !== reqSeq.current) return;
    const b = r.body as { items: NotificationItem[]; unread: number };
    // A new notification arrived while we were already primed → chime + surface it once. Toast the
    // title only (sender/subject), never the message body: when several land between polls the
    // "newest unread" may not be the one that crossed the edge.
    if (primed.current && b.unread > prevUnread.current) {
      playChime();
      const fresh = b.items.find((i) => !i.read);
      if (fresh) toast(fresh.title);
    }
    primed.current = true;
    prevUnread.current = b.unread;
    setItems(b.items);
    setUnread(b.unread);
  }, [toast]);

  // Poll only while logged in; reset cleanly on logout so the next login starts fresh.
  useEffect(() => {
    if (!user) {
      setItems([]);
      setUnread(0);
      setPanel(null);
      prevUnread.current = 0;
      primed.current = false;
      return;
    }
    void reload();
    const iv = window.setInterval(() => void reload(), POLL_MS);
    const onFocus = () => void reload();
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(iv);
      window.removeEventListener('focus', onFocus);
    };
  }, [user, reload]);

  const markRead = useCallback(async (ids?: number[]) => {
    const r = await api<{ unread: number }>('/api/notifications/read', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
    if (!r.ok) return;
    // Invalidate any poll in flight so its pre-mark count can't clobber the authoritative one.
    reqSeq.current++;
    setItems((prev) => prev.map((i) => (!ids || ids.includes(i.id) ? { ...i, read: true } : i)));
    const u = (r.body as { unread: number }).unread;
    setUnread(u);
    prevUnread.current = u;
  }, []);

  const openMessaging = useCallback((sel?: Selection, ref?: MsgRef | null) => {
    setPanel(sel ?? { kind: 'broadcast' });
    setComposeRef(ref ?? null);
  }, []);
  const clearComposeRef = useCallback(() => setComposeRef(null), []);
  const closeMessaging = useCallback(() => {
    setPanel(null);
    setComposeRef(null);
  }, []);

  const value = useMemo<NotificationCtx>(
    () => ({
      items,
      unread,
      reload,
      markRead,
      panel,
      composeRef,
      openMessaging,
      clearComposeRef,
      closeMessaging,
    }),
    [
      items,
      unread,
      reload,
      markRead,
      panel,
      composeRef,
      openMessaging,
      clearComposeRef,
      closeMessaging,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useNotifications(): NotificationCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useNotifications must be used within a NotificationProvider');
  return ctx;
}
